import { Point, VdpElement } from '../types';
import { bezierPointsToSvgPath, pointsToSvgPath, getAnchorTangent } from './vectorPathUtils';
import { elementToSvgPathD } from './pathfinderUtils';

export interface SimplifyOptions {
  /**
   * Distance tolerance in pixels (higher = more aggressive reduction).
   * Range typically 0.5 to 15.0 px. Default: 2.5
   */
  tolerance?: number;

  /**
   * Minimum turning angle in degrees to treat a point as a sharp corner.
   * Corners sharper than this angle will NEVER be eliminated.
   * Default: 45 degrees. Set to 180 to disable corner preservation.
   */
  cornerThresholdDegrees?: number;

  /**
   * Whether to compute smooth cubic Bezier handles for curved segments.
   * Default: true
   */
  smoothCurves?: boolean;

  /**
   * Force closed loop behavior. If undefined, uses element's closedPath property.
   */
  closed?: boolean;
}

export interface SimplifyResult {
  points: Point[];
  pathD: string;
  originalPointsCount: number;
  simplifiedPointsCount: number;
  reductionPercent: number;
}

/**
 * Calculates perpendicular distance from point P to line segment AB
 */
function perpendicularDistance(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;

  if (lenSq < 1e-6) {
    return Math.hypot(p.x - a.x, p.y - a.y);
  }

  // Projection scalar t along segment AB
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;

  return Math.hypot(p.x - projX, p.y - projY);
}

/**
 * Computes turning angle (in degrees) at vertex P between (prev -> P) and (P -> next)
 */
function getTurningAngle(prev: Point, p: Point, next: Point): number {
  const v1x = p.x - prev.x;
  const v1y = p.y - prev.y;
  const v2x = next.x - p.x;
  const v2y = next.y - p.y;

  const len1 = Math.hypot(v1x, v1y);
  const len2 = Math.hypot(v2x, v2y);

  if (len1 < 1e-4 || len2 < 1e-4) return 0;

  const dot = (v1x * v2x + v1y * v2y) / (len1 * len2);
  const clampedDot = Math.max(-1, Math.min(1, dot));
  const rad = Math.acos(clampedDot);
  return (rad * 180) / Math.PI;
}

/**
 * Core Ramer-Douglas-Peucker (RDP) recursive reduction
 */
function rdpSimplify(points: Point[], tolerance: number): Point[] {
  if (points.length <= 2) return points;

  let maxDist = 0;
  let index = 0;
  const first = points[0];
  const last = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i++) {
    const dist = perpendicularDistance(points[i], first, last);
    if (dist > maxDist) {
      maxDist = dist;
      index = i;
    }
  }

  if (maxDist > tolerance) {
    const left = rdpSimplify(points.slice(0, index + 1), tolerance);
    const right = rdpSimplify(points.slice(index), tolerance);
    return left.slice(0, -1).concat(right);
  }

  return [first, last];
}

/**
 * Simplifies a sequence of points with corner-angle preservation and closed-loop support
 */
export function simplifyPoints(
  rawPoints: Point[],
  options: SimplifyOptions = {}
): Point[] {
  if (!rawPoints || rawPoints.length <= 2) return rawPoints || [];

  const tolerance = Math.max(0.1, options.tolerance ?? 2.5);
  const cornerThreshold = options.cornerThresholdDegrees ?? 45;
  const isClosed = options.closed ?? false;

  // Step 1: Remove duplicate adjacent points (within 0.5px)
  const cleaned: Point[] = [rawPoints[0]];
  for (let i = 1; i < rawPoints.length; i++) {
    const prev = cleaned[cleaned.length - 1];
    const curr = rawPoints[i];
    if (Math.hypot(curr.x - prev.x, curr.y - prev.y) > 0.5) {
      cleaned.push(curr);
    }
  }

  if (cleaned.length <= 2) return cleaned;

  // Step 2: Detect essential sharp corners to preserve
  const cornerIndices: number[] = [0];
  const n = cleaned.length;

  for (let i = 1; i < n - 1; i++) {
    const angle = getTurningAngle(cleaned[i - 1], cleaned[i], cleaned[i + 1]);
    if (angle >= cornerThreshold || cleaned[i].type === 'corner') {
      cornerIndices.push(i);
    }
  }

  if (isClosed && n > 2) {
    const lastAngle = getTurningAngle(cleaned[n - 2], cleaned[n - 1], cleaned[0]);
    if (lastAngle >= cornerThreshold) {
      cornerIndices.push(n - 1);
    }
  }

  if (cornerIndices[cornerIndices.length - 1] !== n - 1) {
    cornerIndices.push(n - 1);
  }

  // Step 3: Run RDP between each corner segment
  const simplifiedSegments: Point[][] = [];
  for (let c = 0; c < cornerIndices.length - 1; c++) {
    const startIdx = cornerIndices[c];
    const endIdx = cornerIndices[c + 1];
    const subPoints = cleaned.slice(startIdx, endIdx + 1);

    if (subPoints.length <= 2) {
      simplifiedSegments.push(subPoints);
    } else {
      const simplified = rdpSimplify(subPoints, tolerance);
      simplifiedSegments.push(simplified);
    }
  }

  // Combine segments without duplicate joins
  const merged: Point[] = [];
  for (let s = 0; s < simplifiedSegments.length; s++) {
    const seg = simplifiedSegments[s];
    if (s === 0) {
      merged.push(...seg);
    } else {
      merged.push(...seg.slice(1));
    }
  }

  // Step 4: Handle curve smoothing & Bezier handles calculation
  if (options.smoothCurves) {
    const result: Point[] = [];
    const len = merged.length;

    for (let i = 0; i < len; i++) {
      const cur = { ...merged[i] };
      const prev = i > 0 ? merged[i - 1] : isClosed ? merged[len - 1] : null;
      const next = i < len - 1 ? merged[i + 1] : isClosed ? merged[0] : null;

      let isCorner = false;
      if (prev && next) {
        const angle = getTurningAngle(prev, cur, next);
        if (angle >= cornerThreshold) {
          isCorner = true;
        }
      }

      if (isCorner) {
        cur.type = 'corner';
        delete cur.cp1;
        delete cur.cp2;
      } else if (prev && next) {
        // Compute smooth tangent handles
        const d1 = Math.hypot(cur.x - prev.x, cur.y - prev.y);
        const d2 = Math.hypot(next.x - cur.x, next.y - cur.y);
        const tangent = getAnchorTangent(merged, i, isClosed, 1);

        const handleLen1 = Math.min(d1 * 0.35, 45);
        const handleLen2 = Math.min(d2 * 0.35, 45);

        cur.type = 'smooth';
        cur.cp1 = {
          x: Math.round(cur.x - tangent.dirX * handleLen1),
          y: Math.round(cur.y - tangent.dirY * handleLen1)
        };
        cur.cp2 = {
          x: Math.round(cur.x + tangent.dirX * handleLen2),
          y: Math.round(cur.y + tangent.dirY * handleLen2)
        };
      }

      result.push(cur);
    }
    return result;
  }

  return merged;
}

/**
 * Tokenizes and samples an SVG path string into points
 */
export function sampleSvgPathToPoints(pathD: string, sampleStep: number = 6): Point[] {
  if (!pathD || !pathD.trim()) return [];

  const points: Point[] = [];
  
  // Clean path string and split commands
  const cleanD = pathD.replace(/,/g, ' ').replace(/([a-df-z])/gi, ' $1 ').trim();
  const tokens = cleanD.split(/\s+/).filter(Boolean);

  let curX = 0;
  let curY = 0;
  let startX = 0;
  let startY = 0;
  let i = 0;

  while (i < tokens.length) {
    const cmd = tokens[i++];
    const isRel = cmd === cmd.toLowerCase() && cmd !== 'z';
    const upper = cmd.toUpperCase();

    if (upper === 'M') {
      let x = parseFloat(tokens[i++]);
      let y = parseFloat(tokens[i++]);
      if (isRel) { x += curX; y += curY; }
      curX = x; curY = y;
      startX = x; startY = y;
      points.push({ x: Math.round(curX), y: Math.round(curY), type: 'corner' });

      // Subsequent numbers are implicit LineTos
      while (i < tokens.length && !isNaN(parseFloat(tokens[i]))) {
        let lx = parseFloat(tokens[i++]);
        let ly = parseFloat(tokens[i++]);
        if (isRel) { lx += curX; ly += curY; }
        curX = lx; curY = ly;
        points.push({ x: Math.round(curX), y: Math.round(curY) });
      }
    } else if (upper === 'L') {
      while (i < tokens.length && !isNaN(parseFloat(tokens[i]))) {
        let x = parseFloat(tokens[i++]);
        let y = parseFloat(tokens[i++]);
        if (isRel) { x += curX; y += curY; }
        curX = x; curY = y;
        points.push({ x: Math.round(curX), y: Math.round(curY) });
      }
    } else if (upper === 'H') {
      while (i < tokens.length && !isNaN(parseFloat(tokens[i]))) {
        let x = parseFloat(tokens[i++]);
        if (isRel) x += curX;
        curX = x;
        points.push({ x: Math.round(curX), y: Math.round(curY) });
      }
    } else if (upper === 'V') {
      while (i < tokens.length && !isNaN(parseFloat(tokens[i]))) {
        let y = parseFloat(tokens[i++]);
        if (isRel) y += curY;
        curY = y;
        points.push({ x: Math.round(curX), y: Math.round(curY) });
      }
    } else if (upper === 'C') {
      // Cubic Bezier: x1 y1 x2 y2 x y
      while (i + 5 < tokens.length && !isNaN(parseFloat(tokens[i]))) {
        let x1 = parseFloat(tokens[i++]);
        let y1 = parseFloat(tokens[i++]);
        let x2 = parseFloat(tokens[i++]);
        let y2 = parseFloat(tokens[i++]);
        let x = parseFloat(tokens[i++]);
        let y = parseFloat(tokens[i++]);

        if (isRel) {
          x1 += curX; y1 += curY;
          x2 += curX; y2 += curY;
          x += curX; y += curY;
        }

        // Subdivide cubic curve into sample points
        const chord = Math.hypot(x - curX, y - curY);
        const steps = Math.max(4, Math.min(32, Math.round(chord / sampleStep)));

        for (let s = 1; s <= steps; s++) {
          const t = s / steps;
          const omt = 1 - t;
          const bx = omt * omt * omt * curX + 3 * omt * omt * t * x1 + 3 * omt * t * t * x2 + t * t * t * x;
          const by = omt * omt * omt * curY + 3 * omt * omt * t * y1 + 3 * omt * t * t * y2 + t * t * t * y;
          points.push({ x: Math.round(bx), y: Math.round(by) });
        }

        curX = x; curY = y;
      }
    } else if (upper === 'Q') {
      // Quadratic Bezier: x1 y1 x y
      while (i + 3 < tokens.length && !isNaN(parseFloat(tokens[i]))) {
        let x1 = parseFloat(tokens[i++]);
        let y1 = parseFloat(tokens[i++]);
        let x = parseFloat(tokens[i++]);
        let y = parseFloat(tokens[i++]);

        if (isRel) {
          x1 += curX; y1 += curY;
          x += curX; y += curY;
        }

        const chord = Math.hypot(x - curX, y - curY);
        const steps = Math.max(4, Math.min(24, Math.round(chord / sampleStep)));

        for (let s = 1; s <= steps; s++) {
          const t = s / steps;
          const omt = 1 - t;
          const bx = omt * omt * curX + 2 * omt * t * x1 + t * t * x;
          const by = omt * omt * curY + 2 * omt * t * y1 + t * t * y;
          points.push({ x: Math.round(bx), y: Math.round(by) });
        }

        curX = x; curY = y;
      }
    } else if (upper === 'Z') {
      curX = startX;
      curY = startY;
      if (points.length > 0) {
        points.push({ x: Math.round(startX), y: Math.round(startY) });
      }
    }
  }

  return points;
}

/**
 * Extracts points from any VdpElement (from existing points, pathData, or shape geometry)
 */
export function extractElementPoints(element: VdpElement): { points: Point[]; isClosed: boolean } {
  const isClosed = element.closedPath ?? (element.type !== 'line');

  // 1. Direct point array
  if (element.points && element.points.length > 0) {
    return { points: [...element.points], isClosed };
  }

  // 2. SVG pathData / pathD
  const d = element.pathData || element.pathD || elementToSvgPathD(element);
  if (d && d.trim().length > 0) {
    const sampled = sampleSvgPathToPoints(d, 5);
    if (sampled.length > 0) {
      return { points: sampled, isClosed };
    }
  }

  // 3. Fallback: create points based on bounding box
  const w = element.width || 60;
  const h = element.height || 60;
  return {
    points: [
      { x: 0, y: 0 },
      { x: w, y: 0 },
      { x: w, y: h },
      { x: 0, y: h }
    ],
    isClosed: true
  };
}

/**
 * Simplifies a single element's vector path, returning updated element and metrics
 */
export function simplifyElement(
  element: VdpElement,
  options: SimplifyOptions = {}
): { updatedElement: VdpElement; result: SimplifyResult } {
  const { points: originalPoints, isClosed } = extractElementPoints(element);
  const originalCount = originalPoints.length;

  const effectiveOptions: SimplifyOptions = {
    tolerance: options.tolerance ?? 2.5,
    cornerThresholdDegrees: options.cornerThresholdDegrees ?? 45,
    smoothCurves: options.smoothCurves ?? (element.isSmooth ?? true),
    closed: options.closed ?? isClosed
  };

  const simplifiedPoints = simplifyPoints(originalPoints, effectiveOptions);
  const simplifiedCount = simplifiedPoints.length;

  const reductionPercent = originalCount > 0
    ? Math.max(0, Math.round(((originalCount - simplifiedCount) / originalCount) * 1000) / 10)
    : 0;

  // Generate clean SVG path string
  const hasBeziers = simplifiedPoints.some(p => p.cp1 || p.cp2);
  const newPathD = (effectiveOptions.smoothCurves || hasBeziers)
    ? bezierPointsToSvgPath(simplifiedPoints, effectiveOptions.closed)
    : pointsToSvgPath(simplifiedPoints, effectiveOptions.closed, false);

  const updatedElement: VdpElement = {
    ...element,
    type: (element.type === 'pen' || element.type === 'path') ? element.type : 'path',
    points: simplifiedPoints,
    pathData: newPathD,
    pathD: newPathD,
    closedPath: effectiveOptions.closed,
    isSmooth: effectiveOptions.smoothCurves
  };

  return {
    updatedElement,
    result: {
      points: simplifiedPoints,
      pathD: newPathD,
      originalPointsCount: originalCount,
      simplifiedPointsCount: simplifiedCount,
      reductionPercent
    }
  };
}

/**
 * Simplifies an array of elements
 */
export function simplifyElements(
  elements: VdpElement[],
  options: SimplifyOptions = {}
): {
  updatedElements: VdpElement[];
  totalOriginal: number;
  totalSimplified: number;
  overallReduction: number;
} {
  let totalOriginal = 0;
  let totalSimplified = 0;

  const updatedElements = elements.map(el => {
    const { updatedElement, result } = simplifyElement(el, options);
    totalOriginal += result.originalPointsCount;
    totalSimplified += result.simplifiedPointsCount;
    return updatedElement;
  });

  const overallReduction = totalOriginal > 0
    ? Math.max(0, Math.round(((totalOriginal - totalSimplified) / totalOriginal) * 1000) / 10)
    : 0;

  return {
    updatedElements,
    totalOriginal,
    totalSimplified,
    overallReduction
  };
}
